import {useQuery} from '@github-ui/react-query'
import {useEffect, useEffectEvent, useMemo, useState} from 'react'

import {fetchIssuePickerSelectedIssues, issuePickerSelectedIssuesQueryKey} from './fetch-issue-picker-selected-issues'
import type {IssuePickerItem} from './IssuePicker'

type UseIssuePickerSelectedIssuesOptions = {
  enabled: boolean
  isLoading: boolean
  items: Map<string, IssuePickerItem>
  selectedIssueIds?: string[]
  repositoryNameWithOwner?: string
}

/**
 * Auxiliary read for selected issues that the issue-filtering query does not return. Selected issues
 * are looked up in the current results first and retained afterwards, so they stay visible once a
 * later search no longer matches them.
 */
export function useIssuePickerSelectedIssues({
  enabled,
  isLoading,
  items,
  selectedIssueIds,
  repositoryNameWithOwner,
}: UseIssuePickerSelectedIssuesOptions) {
  const [foundSelectedIssues, setFoundSelectedIssues] = useState<Record<string, IssuePickerItem>>({})
  // Append-only so the query key stays stable across searches and already fetched issues are reused.
  const [requestedIds, setRequestedIds] = useState<string[]>([])

  const syncSelectedIssues = useEffectEvent(() => {
    if (!selectedIssueIds) return

    const found: Record<string, IssuePickerItem> = {}
    const missingIssueIds: string[] = []
    const alreadyRequested = new Set(requestedIds)
    for (const selectedIssueId of selectedIssueIds) {
      const foundSelectedIssue = items.get(selectedIssueId)

      if (foundSelectedIssue) {
        found[selectedIssueId] = foundSelectedIssue
      } else if (!foundSelectedIssues[selectedIssueId] && !alreadyRequested.has(selectedIssueId)) {
        missingIssueIds.push(selectedIssueId)
      }
    }

    if (Object.keys(found).length > 0) {
      setFoundSelectedIssues(currentFoundSelectedIssues => {
        const hasChanges = Object.entries(found).some(([id, issue]) => currentFoundSelectedIssues[id] !== issue)
        return hasChanges ? {...currentFoundSelectedIssues, ...found} : currentFoundSelectedIssues
      })
    }
    if (missingIssueIds.length > 0) {
      setRequestedIds(currentRequestedIds => [...currentRequestedIds, ...missingIssueIds])
    }
  })

  useEffect(() => {
    // Wait until the issue-filtering query settles before looking for missing selected issues.
    if (!enabled || isLoading) return

    // eslint-disable-next-line react-hooks/set-state-in-effect
    syncSelectedIssues()
  }, [enabled, isLoading, items])

  const [owner, repo] = (repositoryNameWithOwner || '').split('/')
  const query = useQuery({
    queryKey: issuePickerSelectedIssuesQueryKey(owner || '', repo || '', requestedIds),
    queryFn: ({signal}) => fetchIssuePickerSelectedIssues(owner || '', repo || '', requestedIds, signal),
    enabled: enabled && requestedIds.length > 0 && Boolean(owner && repo),
    // Keep previously fetched selected issues rendered while a newly selected issue is fetched.
    placeholderData: previousData => previousData,
  })

  const selectedIssues = useMemo(() => {
    const merged: Record<string, IssuePickerItem> = {...foundSelectedIssues}
    for (const issue of query.data || []) {
      merged[issue.id] = issue
    }
    return merged
  }, [foundSelectedIssues, query.data])

  return {selectedIssues, isError: query.isError, refetch: query.refetch}
}
